import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../UI/Searchbar'
import OrderWizardMSSP from '../OrderWizard/OrderWizardMSSP';
import usePost from '../../Hooks/usePost';
import useGet from '../../Hooks/useGet';
import API_ENDPOINTS from '../../Config/api';
import { FaEye, FaPencilAlt } from 'react-icons/fa';
import ChannelBadge from '../common/ChannelBadge';
import useUserType from '../../Hooks/useUserType';

const displayStato = (stato, isReseller) => {
  if (!isReseller) return stato;
  if (stato === 'APERTO') return 'DA ATTIVARE';
  if (stato === 'DA FATTURARE') return 'ATTIVATO';
  return stato;
};

export default function ListOrdiniTable({
  data = [],
  setData,
  refetchData,
  middleContent = null,
  searchShrinkCm = 0,
  exportHandler = null,
  showCreateButton = true,
  userIsTecnico = false,
}) {
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { postData } = usePost();
  const { isReseller } = useUserType();
  const { fetchedData: settori } = useGet(API_ENDPOINTS.getSettori);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const navigate = useNavigate();

  const filteredData = data?.filter((item) => {
    const s = search.toLowerCase();
    return (item.numero_ordine ?? '').toLowerCase().includes(s) ||
           (item.stato_ordine ?? '').toLowerCase().includes(s) ||
           (item.email_contatto ?? '').toLowerCase().includes(s);
  });

  const handleCreateSuccess = async (response) => {
    setShowCreateModal(false);
    if (response && response.id_ordine) {
      setData(prevData => [...(prevData || []), response]);
    }
    await refetchData();
  };

  // Se showCreateModal è true, mostra il form invece della tabella
  if (showCreateModal) {
    return (
      <div>
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Crea Nuovo Ordine</h2>
          <button
            onClick={() => setShowCreateModal(false)}
            className="flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95"
          >
            Torna agli Ordini
          </button>
        </div>
        <OrderWizardMSSP
          onSubmitSuccess={handleCreateSuccess}
          onClose={() => setShowCreateModal(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchBar
            search={search}
            setSearch={setSearch}
            placeholder="Cerca ordini..."
            shrinkCm={searchShrinkCm}
          />
        </div>

        <div className="flex-1 flex justify-center">
          {middleContent}
        </div>

        <div className="flex-none">
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (exportHandler && typeof exportHandler === 'function') {
                  exportHandler();
                } else {
                  navigate('/ordini/riepilogo');
                }
              }}
              className="flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95"
            >
              {exportHandler ? 'Esporta PDF' : 'Visualizza Riepilogo Ordini'}
            </button>

            {showCreateButton && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95"
              >
                Aggiungi Ordine
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col h-[32rem] ">
        <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable] border-[#D1D5DB] border rounded">
          <table className="w-full table-auto text-sm border-collapse">
                <colgroup>
                  <col className="w-[14%]" />
                  <col className="w-[16%]" />
                  <col className="w-[13%]" />
                  <col className="w-[12%]" />
                  <col className="w-[12%]" />
                  <col className="w-[9%]" />
                  <col className="w-[14%]" />
                  <col className="w-[10%]" />
                </colgroup>

            <thead className="text-black text-sm sticky top-0 z-10 bg-[#F3F4F7]">
              <tr>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Numero Ordine</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Nome Azienda</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">P.IVA</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Stato</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Canale</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Endpoint</th>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">Totale (IVA incl.)</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">Azioni</th>
              </tr>
            </thead>

            <tbody>
              {filteredData && filteredData.length > 0 ? (
                filteredData.map((item, i) => (
                  <Row 
                    key={item.id_ordine} 
                    item={item} 
                    refetchData={refetchData} 
                    settori={settori}
                    onEdit={setEditingOrder}
                    onView={setViewingOrder}
                    isReseller={isReseller}
                  />
                ))
              ) : (
                <tr>
                    <td colSpan={8} className="text-center py-6 text-slate-400">
                    Nessun ordine trovato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals rendered outside table */}
      {editingOrder && (
        <ModificaOrdineModal
          ordine={editingOrder}
          onClose={() => setEditingOrder(null)}
          refetchData={refetchData}
          settori={settori}
          isReseller={isReseller}
        />
      )}
      {viewingOrder && (
        <DettagliOrdineModal
          ordine={viewingOrder}
          onClose={() => setViewingOrder(null)}
          isReseller={isReseller}
        />
      )}
    </div>
  );
}

function Row({ item, refetchData, settori, onEdit, onView, isReseller }) {
  return (
    <>
      <tr className="border-b border-gray-200 hover:bg-gray-50">
        <td className="px-3 py-4 text-black">{item.numero_ordine}</td>
        <td className="px-3 py-4 text-black">{item.azienda_info?.nome_azienda || '-'}</td>
        <td className="px-3 py-4 text-black">{item.azienda_info?.partita_iva || '-'}</td>
        <td className="px-3 py-4 text-black">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.stato_ordine === 'APERTO' ? 'bg-yellow-100 text-yellow-800' :
            item.stato_ordine === 'DA FATTURARE' ? 'bg-blue-100 text-blue-800' :
            item.stato_ordine === 'AUTORINNOVO' ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {displayStato(item.stato_ordine, isReseller)}
          </span>
        </td>
        <td className="px-3 py-4">
          <ChannelBadge channelType={item.channel_type} resellerInfo={item.reseller_info} />
        </td>
        <td className="px-3 py-4 text-black">{item.numero_endpoint}</td>
        <td className="px-3 py-4 text-black">{item.totale_importo} €</td>
        <td className="px-3 py-4 text-black">
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => onView(item)}
              className="text-blue-600 hover:text-blue-800"
              title="Visualizza dettagli"
            >
              <FaEye size={18} />
            </button>
            {item.stato_ordine === 'APERTO' && (
              <button
                onClick={() => onEdit(item)}
                className="text-green-600 hover:text-green-800"
                title="Modifica ordine"
              >
                <FaPencilAlt size={16} />
              </button>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}

function ModificaOrdineModal({ ordine, onClose, refetchData, settori, isReseller }) {
  const [formData, setFormData] = useState({
    numero_endpoint: ordine.numero_endpoint || 0,
    data_rinnovo: ordine.data_rinnovo || '',
    email_contatto: ordine.email_contatto || '',
    telefono_contatto: ordine.telefono_contatto || '',
    nome_azienda: ordine.azienda_info?.nome_azienda || '',
    partita_iva: ordine.azienda_info?.partita_iva || '',
    pec: ordine.azienda_info?.pec || '',
    url: ordine.azienda_info?.url || '',
    indirizzo: ordine.azienda_info?.indirizzo || '',
    cap: ordine.azienda_info?.cap || '',
    citta: ordine.azienda_info?.citta || '',
    comune: ordine.azienda_info?.comune || '',
    provincia: ordine.azienda_info?.provincia || '',
    stato: ordine.azienda_info?.stato || '',
    telefono: ordine.azienda_info?.telefono || '',
    numero_dipendenti: ordine.azienda_info?.numero_dipendenti || '',
    fatturato_annuo: ordine.azienda_info?.fatturato_annuo || '',
    id_settore: ordine.azienda_info?.id_settore || '',
    codice_ateco: ordine.azienda_info?.codice_ateco || '',
  });
  const [saving, setSaving] = useState(false);
  const { postData } = usePost();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (saving) return;
    
    try {
      setSaving(true);
      // Usa PATCH per aggiornare l'ordine con l'endpoint update-ordine
      await postData(
        `${API_ENDPOINTS.myOrdiniMSSP}${ordine.id_ordine}/update-ordine/`,
        formData,
        'PATCH'
      );
      await refetchData();
      onClose();
    } catch (error) {
      console.error('Errore aggiornamento ordine:', error);
      alert('Errore nell\'aggiornamento dell\'ordine');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Modifica Ordine</h2>
        <p className="text-sm text-gray-600 mb-4">
          Ordine: {ordine.numero_ordine}
        </p>
        
        {/* Sezione Dati Azienda */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Dati Azienda</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nome Azienda *</label>
              <input
                type="text"
                name="nome_azienda"
                value={formData.nome_azienda}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">P.IVA *</label>
              <input
                type="text"
                name="partita_iva"
                value={formData.partita_iva}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                minLength={11}
                maxLength={11}
                pattern="[0-9]{11}"
                title="Inserire una Partita IVA valida (11 cifre, senza prefisso IT)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">PEC</label>
              <input
                type="email"
                name="pec"
                value={formData.pec}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">URL Aziendale</label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Indirizzo</label>
              <input
                type="text"
                name="indirizzo"
                value={formData.indirizzo}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">CAP</label>
              <input
                type="text"
                name="cap"
                value={formData.cap}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Città</label>
              <input
                type="text"
                name="citta"
                value={formData.citta}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Comune</label>
              <input
                type="text"
                name="comune"
                value={formData.comune}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Provincia</label>
              <input
                type="text"
                name="provincia"
                value={formData.provincia}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Stato</label>
              <input
                type="text"
                name="stato"
                value={formData.stato}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Telefono</label>
              <input
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Numero Dipendenti</label>
              <input
                type="number"
                name="numero_dipendenti"
                value={formData.numero_dipendenti}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Fatturato Annuo (€)</label>
              <input
                type="number"
                name="fatturato_annuo"
                value={formData.fatturato_annuo}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Settore</label>
              <select
                name="id_settore"
                value={formData.id_settore}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Seleziona Settore</option>
                {settori && settori.map((s) => (
                  <option key={s.id_settore} value={s.id_settore}>
                    {s.settore}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Codice ATECO</label>
              <input
                type="text"
                name="codice_ateco"
                value={formData.codice_ateco}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Sezione Dati Ordine */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Dati Ordine</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Numero Endpoint *</label>
              <input
                type="number"
                name="numero_endpoint"
                value={formData.numero_endpoint}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                min="1"
                required
                title="Inserire il numero di endpoint (minimo 1)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Data Rinnovo *</label>
              <input
                type="date"
                name="data_rinnovo"
                value={formData.data_rinnovo}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email Contatto</label>
              <input
                type="email"
                name="email_contatto"
                value={formData.email_contatto}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                title="Inserire un indirizzo email valido"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Telefono Contatto</label>
              <input
                type="tel"
                name="telefono_contatto"
                value={formData.telefono_contatto}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                pattern="[+]?[0-9\s-]{6,20}"
                title="Inserire un numero di telefono valido (6-20 caratteri, può includere +, spazi e trattini)"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-3 rounded mb-4">
          <p className="text-sm text-gray-600">
            <strong>Stato attuale:</strong> <span className={`px-2 py-1 rounded text-xs ${
              ordine.stato_ordine === 'APERTO' ? 'bg-yellow-100 text-yellow-800' :
              ordine.stato_ordine === 'DA FATTURARE' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>{displayStato(ordine.stato_ordine, isReseller)}</span> (non modificabile)
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DettagliOrdineModal({ ordine, onClose, isReseller }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Dettagli Ordine</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Numero Ordine</p>
            <p className="text-sm font-semibold">{ordine.numero_ordine}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Stato</p>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              ordine.stato_ordine === 'APERTO' ? 'bg-yellow-100 text-yellow-800' :
              ordine.stato_ordine === 'DA FATTURARE' ? 'bg-blue-100 text-blue-800' :
              ordine.stato_ordine === 'AUTORINNOVO' ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {displayStato(ordine.stato_ordine, isReseller)}
            </span>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Nome Azienda</p>
            <p className="text-sm font-semibold">{ordine.azienda_info?.nome_azienda || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">P.IVA</p>
            <p className="text-sm font-semibold">{ordine.azienda_info?.partita_iva || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Numero Endpoint</p>
            <p className="text-sm font-semibold">{ordine.numero_endpoint}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Endpoint Attivi</p>
            <p className="text-sm font-semibold">{ordine.numero_endpoint_attivi || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Importo €</p>
            <p className="text-sm font-semibold">{ordine.importo_euro} €</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">IVA %</p>
            <p className="text-sm font-semibold">{ordine.iva}%</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Totale (IVA incl.)</p>
            <p className="text-sm font-semibold text-green-600">{ordine.totale_importo} €</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Data Rinnovo</p>
            <p className="text-sm font-semibold">{ordine.data_rinnovo || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Data Limite Disdetta</p>
            <p className="text-sm font-semibold">{ordine.data_limite_disdetta_ordine || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-1">Data Pagamento</p>
            <p className="text-sm font-semibold">{ordine.data_pagamento || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded col-span-2">
            <p className="text-xs text-gray-500 mb-1">Email Contatto</p>
            <p className="text-sm font-semibold">{ordine.email_contatto || '-'}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded col-span-2">
            <p className="text-xs text-gray-500 mb-1">Telefono Contatto</p>
            <p className="text-sm font-semibold">{ordine.telefono_contatto || '-'}</p>
          </div>

          {ordine.channel_type && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Canale</p>
              <ChannelBadge channelType={ordine.channel_type} size="md" />
            </div>
          )}

          {ordine.reseller_info && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Reseller / Partner</p>
              <p className="text-sm font-semibold">{ordine.reseller_info.nome_mssp}</p>
              {ordine.reseller_info.codice_referral && (
                <p className="text-xs text-gray-400">Codice: {ordine.reseller_info.codice_referral}</p>
              )}
            </div>
          )}

          {ordine.prodotto_base_info && (
            <div className="bg-blue-50 p-3 rounded col-span-2">
              <p className="text-xs text-blue-600 mb-1">Prodotto Base</p>
              <p className="text-sm font-semibold">{ordine.prodotto_base_info.nome_prodotto}</p>
              <p className="text-xs text-gray-500">Codice: {ordine.prodotto_base_info.codice_prodotto}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}